import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Link,
} from "@mui/material";

interface TermsModalProps {
  open: boolean;
  onClose: () => void;
}

export const TermsModal: React.FC<TermsModalProps> = ({ open, onClose }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" scroll="paper">
      <DialogTitle sx={{ fontWeight: "bold" }}>Terms of Service</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body1">
            <strong>Effective Date: April 16, 2026</strong>
          </Typography>
          <Typography variant="body1">
            These terms govern your use of the Obsidian Ridge website at obsidianridge.ai and all
            related services provided by Obsidian Ridge Holdings, LLC (&quot;Obsidian Ridge,&quot;
            &quot;we,&quot; &quot;us&quot;). By accessing or using our services, you agree to be
            bound by these terms. If you do not agree, do not use the services.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            1. Accounts
          </Typography>
          <Typography variant="body2">
            You must provide accurate information when creating an account and keep your credentials
            secure. You are responsible for all activity under your account. You must be at least 16
            years old to create an account. Notify us immediately if you believe your account has
            been compromised.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            2. Courses, Certifications & Content
          </Typography>
          <Typography variant="body2">
            We offer both free and paid courses and certification exams. Upon enrollment, you
            receive a personal, non-transferable license to access the course materials for your own
            professional development. Certification credentials are issued based on exam performance
            and may have expiration dates as stated at the time of issuance.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            3. Intellectual Property
          </Typography>
          <Typography variant="body2">
            All courseware, platform code, trademarks, and content on this site are owned by
            Obsidian Ridge or its licensors. You may not copy, redistribute, resell, or create
            derivative works from any content without our prior written consent.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            4. User Conduct
          </Typography>
          <Typography variant="body2">
            You agree not to: share account credentials or course access with others; attempt to
            circumvent access controls or security measures; submit false information; use the
            platform for any unlawful purpose; or interfere with the operation of the services.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            5. Payments & Refunds
          </Typography>
          <Typography variant="body2">
            Paid courses and certifications are processed through Stripe. Prices are displayed at
            checkout and charged in the indicated currency. Subscriptions renew automatically unless
            you cancel before the next billing cycle. We do not offer refunds for completed courses
            or issued certifications. If you experience a technical issue preventing access to paid
            content, contact us within 14 days of purchase.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            6. Termination
          </Typography>
          <Typography variant="body2">
            We may suspend or terminate your account for violation of these terms, non-payment, or
            misuse. You may delete your account at any time by contacting us. Upon termination, your
            right to access paid content ends immediately. Earned and valid certification
            credentials remain yours.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            7. Disclaimers
          </Typography>
          <Typography variant="body2">
            The services are provided &quot;as is&quot; and &quot;as available.&quot; We make no
            warranties, express or implied, regarding availability, accuracy, or fitness for a
            particular purpose. Course content is educational and does not constitute professional
            advice.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            8. Limitation of Liability
          </Typography>
          <Typography variant="body2">
            To the maximum extent permitted by law, our total liability for any claim arising from
            your use of the services is limited to the amount you paid us in the 12 months preceding
            the claim. We are not liable for indirect, incidental, special, or consequential
            damages.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            9. Governing Law
          </Typography>
          <Typography variant="body2">
            These terms are governed by the laws of the State of Wyoming, United States, without
            regard to conflict-of-law principles. Any dispute shall be resolved in the state or
            federal courts located in Wyoming.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            10. Changes to These Terms
          </Typography>
          <Typography variant="body2">
            We may update these terms from time to time. Material changes will be communicated via a
            notice on the site. Continued use after changes take effect constitutes acceptance. We
            encourage you to review this page periodically.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            11. Contact
          </Typography>
          <Typography variant="body2">
            Questions about these terms? Email{" "}
            <Link href="mailto:contact@obsidianridge.ai">contact@obsidianridge.ai</Link> or use our
            Contact page.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
